import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import {
  Settings as SettingsIcon,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  SlidersHorizontal,
  ToggleLeft,
  Type,
  Hash,
  Boxes,
  Loader2,
  KeyRound,
  X,
  Eye,
  EyeOff,
  Lock,
} from 'lucide-react';
import {
  adminSettingsRequest,
  adminSettingsResponse,
  AdminSettingsCatalogEntry,
  initialAdminSettingsRequest,
} from '../Types/AdminType';
import {
  deleteAdminSetting,
  createAdminSetting,
  updateAdminSetting,
  getAllAdminSettings,
  getAdminSettingsCatalog,
  revealAdminSetting,
} from '../Services/AdminService';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';

const DATA_TYPES = ['TEXT', 'NUMBER', 'BOOLEAN', 'SECRET'];

const inputClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-sm font-medium text-slate-100 placeholder:text-slate-500 focus:border-cyan-500/60 focus:outline-none focus:ring-1 focus:ring-cyan-500/40 transition-colors';

const labelClass = 'mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-slate-400';

const Toggle = ({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
      checked ? 'bg-cyan-500' : 'bg-slate-700'
    }`}
  >
    <span
      className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-5' : ''
      }`}
    />
  </button>
);

const typeIcon = (type: string) => {
  if (type === 'BOOLEAN') return <ToggleLeft size={16} />;
  if (type === 'NUMBER') return <Hash size={16} />;
  if (type === 'SECRET') return <Lock size={16} />;
  return <Type size={16} />;
};

const AdminSettingsComponent = () => {
  const [items, setItems] = useState<adminSettingsResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingKey, setSavingKey] = useState<string>('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<adminSettingsResponse | null>(null);
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const [catalog, setCatalog] = useState<AdminSettingsCatalogEntry[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [secretRevealed, setSecretRevealed] = useState(false);

  const { values, setAllValues, setField, handleInputChange, handleValueChange } =
    useFormController<adminSettingsRequest>(initialAdminSettingsRequest);

  const loadSettings = async () => {
    setAlert(null);
    setLoading(true);
    try {
      const res = await getAllAdminSettings();
      if (res.success && Array.isArray(res.data)) {
        setItems(res.data.sort((a, b) => a.settingsKey.localeCompare(b.settingsKey)));
      } else {
        setAlert({
          variant: 'error',
          title: 'Load Error',
          message: res.message || 'Could not retrieve admin settings.',
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } catch {
      setAlert({
        variant: 'error',
        title: 'Load Error',
        message: 'An unexpected error occurred while loading the settings.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCatalog = async () => {
    setCatalogLoading(true);
    try {
      const res = await getAdminSettingsCatalog();
      if (res.success && Array.isArray(res.data)) {
        setCatalog(res.data);
      }
    } finally {
      setCatalogLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAdd = () => {
    setAllValues(initialAdminSettingsRequest);
    setSecretRevealed(false);
    setIsAddOpen(true);
  };

  const openEdit = async (item: adminSettingsResponse) => {
    setSelected(item);
    let value = item.settingsValue;

    if (item.dataType === 'SECRET' && item.encrypted) {
      setSecretRevealed(false);
      try {
        const res = await revealAdminSetting(item.id);
        if (res.success && res.data) {
          value = res.data.settingsValue;
          setSecretRevealed(true);
        } else {
          setAlert({
            variant: 'error',
            title: 'Reveal Error',
            message: res.message || 'Could not reveal secret value.',
            autoCloseMs: 5000,
            onClose: () => setAlert(null),
          });
          return;
        }
      } catch {
        setAlert({
          variant: 'error',
          title: 'Reveal Error',
          message: 'Could not reveal secret value.',
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
        return;
      }
    } else {
      setSecretRevealed(false);
    }

    setAllValues({
      settingsKey: item.settingsKey,
      settingsValue: value,
      dataType: item.dataType,
      description: item.description,
      isActive: item.isActive,
    });
    setIsEditOpen(true);
  };

  const handleCreate = async () => {
    setAlert(null);
    setSaving(true);
    try {
      const res = await createAdminSetting(values);
      if (res.success) {
        setIsAddOpen(false);
        setAlert({
          variant: 'success',
          title: 'Success...!',
          message: `Setting "${res.data.settingsKey}" has been added successfully.`,
          autoCloseMs: 4000,
          onClose: () => setAlert(null),
        });
        await loadSettings();
      } else {
        setAlert({
          variant: 'error',
          title: 'Registration Error',
          message: res.message,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } catch {
      setAlert({
        variant: 'error',
        title: 'Registration Error',
        message: 'An unexpected error occurred.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selected) return;
    setAlert(null);
    setSaving(true);
    try {
      const res = await updateAdminSetting(values, selected.id);
      if (res.success) {
        setIsEditOpen(false);
        setAlert({
          variant: 'success',
          title: 'Success...!',
          message: `Setting "${res.data.settingsKey}" has been updated successfully.`,
          autoCloseMs: 4000,
          onClose: () => setAlert(null),
        });
        await loadSettings();
      } else {
        setAlert({
          variant: 'error',
          title: 'Update Error',
          message: res.message,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } catch {
      setAlert({
        variant: 'error',
        title: 'Update Error',
        message: 'An unexpected error occurred.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    setAlert(null);
    setSaving(true);
    try {
      const res = await deleteAdminSetting(selected.id);
      if (res.success) {
        setIsDeleteOpen(false);
        setAlert({
          variant: 'success',
          title: 'Success...!',
          message: `Setting "${selected.settingsKey}" has been deleted.`,
          autoCloseMs: 4000,
          onClose: () => setAlert(null),
        });
        setItems((prev) => prev.filter((i) => i.id !== selected.id));
      } else {
        setAlert({
          variant: 'error',
          title: 'Delete Error',
          message: res.message,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } catch {
      setAlert({
        variant: 'error',
        title: 'Delete Error',
        message: 'An unexpected error occurred.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleBooleanValue = async (item: adminSettingsResponse, nextValue: boolean) => {
    setAlert(null);
    setSavingKey(item.id);
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, settingsValue: String(nextValue) } : i)));
    try {
      const res = await updateAdminSetting(
        {
          settingsKey: item.settingsKey,
          settingsValue: String(nextValue),
          dataType: item.dataType,
          description: item.description,
          isActive: item.isActive,
        },
        item.id
      );
      if (!res.success) {
        await loadSettings();
        setAlert({
          variant: 'error',
          title: 'Update Error',
          message: res.message,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } catch {
      await loadSettings();
      setAlert({
        variant: 'error',
        title: 'Update Error',
        message: 'An unexpected error occurred.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setSavingKey('');
    }
  };

  const toggleActive = async (item: adminSettingsResponse, nextValue: boolean) => {
    setAlert(null);
    setSavingKey(item.id + 'active');
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, isActive: nextValue } : i)));
    try {
      const res = await updateAdminSetting(
        {
          settingsKey: item.settingsKey,
          settingsValue: item.settingsValue,
          dataType: item.dataType,
          description: item.description,
          isActive: nextValue,
        },
        item.id
      );
      if (!res.success) {
        await loadSettings();
        setAlert({
          variant: 'error',
          title: 'Update Error',
          message: res.message,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } catch {
      await loadSettings();
      setAlert({
        variant: 'error',
        title: 'Update Error',
        message: 'An unexpected error occurred.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setSavingKey('');
    }
  };

  const activeCount = items.filter((i) => i.isActive).length;

  const stats = [
    { label: 'Total Settings', value: items.length, icon: <Boxes size={18} />, accent: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400' },
    { label: 'Active Settings', value: activeCount, icon: <ToggleLeft size={18} />, accent: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' },
    { label: 'Booleans', value: items.filter((i) => i.dataType === 'BOOLEAN').length, icon: <SlidersHorizontal size={18} />, accent: 'border-violet-500/30 bg-violet-500/10 text-violet-400' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} />}

      <div className="mx-auto max-w-6xl space-y-8">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
              <SettingsIcon size={26} />
            </span>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-slate-100 md:text-3xl">Admin Settings</h1>
              <p className="mt-1 text-sm font-medium text-slate-400">Platform-wide configuration stored in the database.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadSettings}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800/80 px-4 py-2.5 text-sm font-bold text-slate-200 transition-colors hover:border-cyan-500/40 hover:text-cyan-300 disabled:opacity-50"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={openAdd}
              className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-black text-slate-950 transition-all hover:bg-cyan-400 active:scale-95"
            >
              <Plus size={16} /> Add Setting
            </button>
          </div>
        </header>

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {stats.map((stat) => (
            <div key={stat.label} className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
              <div className="flex items-center justify-between">
                <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${stat.accent}`}>{stat.icon}</span>
                <span className="text-2xl font-black text-slate-100">{loading ? '—' : stat.value}</span>
              </div>
              <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">{stat.label}</p>
            </div>
          ))}
        </section>

        {loading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-36 animate-pulse rounded-2xl border border-slate-800 bg-slate-900/40" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-800 bg-slate-900/40 px-6 py-16 text-center">
            <SettingsIcon size={40} className="text-slate-600" />
            <p className="mt-4 text-base font-bold text-slate-300">No settings configured yet</p>
            <p className="mt-1 text-sm text-slate-500">Add your first setting to start storing configuration in the database.</p>
            <button onClick={openAdd} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-black text-slate-950 transition-all hover:bg-cyan-400">
              <Plus size={16} /> Add Setting
            </button>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 gap-4 md:grid-cols-2"
          >
            {items.map((item) => (
              <div key={item.id} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 transition-colors hover:border-cyan-500/30">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className={`flex h-10 w-10 items-center justify-center rounded-xl border ${
                      item.dataType === 'BOOLEAN'
                        ? 'border-violet-500/30 bg-violet-500/10 text-violet-400'
                        : item.dataType === 'NUMBER'
                        ? 'border-amber-500/30 bg-amber-500/10 text-amber-400'
                        : item.dataType === 'SECRET'
                        ? 'border-rose-500/30 bg-rose-500/10 text-rose-400'
                        : 'border-sky-500/30 bg-sky-500/10 text-sky-400'
                    }`}>
                      {typeIcon(item.dataType)}
                    </span>
                    <div>
                      <h3 className="text-sm font-black text-slate-100">{item.settingsKey}</h3>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{item.dataType || 'TEXT'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEdit(item)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-700 bg-slate-800/60 text-slate-300 transition-colors hover:border-cyan-500/40 hover:text-cyan-300"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => {
                        setSelected(item);
                        setIsDeleteOpen(true);
                      }}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-700 bg-slate-800/60 text-slate-300 transition-colors hover:border-rose-500/40 hover:text-rose-300"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {item.description && (
                  <p className="mt-3 text-xs font-medium text-slate-500">{item.description}</p>
                )}

                <div className="mt-4 flex items-center justify-between gap-4 border-t border-slate-800 pt-4">
                  {item.dataType === 'BOOLEAN' ? (
                    <div className="flex items-center gap-3">
                      <Toggle
                        checked={item.settingsValue === 'true'}
                        disabled={savingKey === item.id}
                        onChange={(v) => toggleBooleanValue(item, v)}
                      />
                      <span className="text-xs font-bold text-slate-300">
                        {item.settingsValue === 'true' ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  ) : item.dataType === 'SECRET' ? (
                    <div className="flex items-center gap-2">
                      <span className="truncate font-mono text-xs font-semibold text-rose-300">
                        {item.settingsValue || '—'}
                      </span>
                      {item.encrypted && (
                        <span className="rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest text-emerald-400">
                          AES
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="truncate font-mono text-xs font-semibold text-cyan-300">
                      {item.settingsValue || '—'}
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${item.isActive ? 'text-emerald-400' : 'text-slate-500'}`}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <Toggle
                      checked={Boolean(item.isActive)}
                      disabled={savingKey === item.id + 'active'}
                      onChange={(v) => toggleActive(item, v)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </div>

      {/* ADD / EDIT MODAL */}
      <Transition.Root show={isAddOpen || isEditOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => { setIsAddOpen(false); setIsEditOpen(false); }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-end justify-center p-4 sm:items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-3xl border border-slate-700 bg-slate-900 p-7 shadow-2xl">
                <div className="flex items-center justify-between">
                  <Dialog.Title className="text-xl font-black text-slate-100">
                    {isEditOpen ? `Edit ${selected?.settingsKey || 'Setting'}` : 'Add Setting'}
                  </Dialog.Title>
                  <button
                    onClick={() => { setIsAddOpen(false); setIsEditOpen(false); }}
                    className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-700 text-slate-400 transition-colors hover:text-white"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="mt-6 space-y-5">
                  {isAddOpen && (
                    <div>
                      <label className={labelClass}>Known Setting</label>
                      <select
                        className={inputClass}
                        value=""
                        disabled={catalogLoading}
                        onChange={(e) => {
                          const key = e.target.value;
                          if (!key) return;
                          const entry = catalog.find((c) => c.key === key);
                          if (entry) {
                            setField('settingsKey', entry.key);
                            setField('description', entry.description);
                            setField('dataType', entry.dataType);
                          }
                        }}
                      >
                        <option value="" className="bg-slate-900">
                          {catalogLoading ? 'Loading catalog...' : 'Choose a known setting (optional)'}
                        </option>
                        {catalog.map((entry) => (
                          <option key={entry.key} value={entry.key} className="bg-slate-900">
                            {entry.label}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-[10px] font-semibold text-slate-500">
                        Selecting a known setting auto-fills its description and data type.
                      </p>
                    </div>
                  )}

                  <div>
                    <label className={labelClass}>Settings Key</label>
                    <div className="relative">
                      <KeyRound size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                      <input
                        className={`${inputClass} pl-10`}
                        placeholder="e.g. maintenanceMode"
                        value={values.settingsKey}
                        disabled={isEditOpen}
                        onChange={handleInputChange('settingsKey')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Data Type</label>
                    <select
                      className={inputClass}
                      value={values.dataType}
                      onChange={(e) => handleValueChange('dataType')(e.target.value)}
                    >
                      {DATA_TYPES.map((type) => (
                        <option key={type} value={type} className="bg-slate-900">
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Value</label>
                    {values.dataType === 'BOOLEAN' ? (
                      <div className="flex items-center gap-3 pt-1">
                        <Toggle
                          checked={values.settingsValue === 'true'}
                          onChange={(v) => setField('settingsValue', String(v))}
                        />
                        <span className="text-sm font-bold text-slate-300">
                          {values.settingsValue === 'true' ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    ) : values.dataType === 'SECRET' ? (
                      <div className="relative">
                        <input
                          type={secretRevealed || isAddOpen ? 'text' : 'password'}
                          className={`${inputClass} pr-12`}
                          placeholder={isEditOpen ? '••••••' : 'Enter secret value'}
                          value={values.settingsValue}
                          onChange={handleInputChange('settingsValue')}
                        />
                        {!isAddOpen && (
                          <button
                            type="button"
                            onClick={() => setSecretRevealed((prev) => !prev)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-cyan-400"
                          >
                            {secretRevealed ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        )}
                      </div>
                    ) : (
                      <input
                        type={values.dataType === 'NUMBER' ? 'number' : 'text'}
                        className={inputClass}
                        placeholder={values.dataType === 'NUMBER' ? 'e.g. 10' : 'e.g. true / message text'}
                        value={values.settingsValue}
                        onChange={handleInputChange('settingsValue')}
                      />
                    )}
                  </div>

                  <div>
                    <label className={labelClass}>Description</label>
                    <textarea
                      className={`${inputClass} min-h-[80px] resize-none`}
                      placeholder="What does this setting control?"
                      value={values.description}
                      onChange={handleInputChange('description')}
                    />
                    {(() => {
                      const matched = catalog.find((c) => c.key === values.settingsKey);
                      return matched?.description && !values.description ? (
                        <p className="mt-1 text-[10px] font-semibold text-slate-500">{matched.description}</p>
                      ) : null;
                    })()}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-slate-300">Active</span>
                    <Toggle checked={Boolean(values.isActive)} onChange={(v) => setField('isActive', v)} />
                  </div>
                </div>

                <div className="mt-7 flex justify-end gap-3">
                  <button
                    onClick={() => { setIsAddOpen(false); setIsEditOpen(false); }}
                    className="rounded-xl border border-slate-700 px-5 py-3 text-sm font-bold text-slate-300 transition-colors hover:bg-slate-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={isEditOpen ? handleUpdate : handleCreate}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-3 text-sm font-black text-slate-950 transition-all hover:bg-cyan-400 active:scale-95 disabled:opacity-50"
                  >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                    {saving ? 'Saving...' : isEditOpen ? 'Save Changes' : 'Add Setting'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* DELETE MODAL */}
      <Transition.Root show={isDeleteOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsDeleteOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-end justify-center p-4 sm:items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-3xl border border-slate-700 bg-slate-900 p-7 text-center shadow-2xl">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-rose-500/30 bg-rose-500/10 text-rose-400">
                  <Trash2 size={24} />
                </span>
                <Dialog.Title className="mt-4 text-xl font-black text-slate-100">Delete Setting?</Dialog.Title>
                <p className="mt-2 text-sm font-medium text-slate-400">
                  Are you sure you want to delete{' '}
                  <span className="font-black text-slate-100">{selected?.settingsKey}</span>? This cannot be undone.
                </p>
                <div className="mt-7 flex gap-3">
                  <button
                    onClick={() => setIsDeleteOpen(false)}
                    className="flex-1 rounded-xl border border-slate-700 px-4 py-3 text-sm font-bold text-slate-300 transition-colors hover:bg-slate-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={saving}
                    className="flex-1 rounded-xl bg-rose-500 px-4 py-3 text-sm font-black text-white transition-all hover:bg-rose-400 active:scale-95 disabled:opacity-50"
                  >
                    {saving ? <Loader2 size={16} className="mx-auto animate-spin" /> : 'Delete'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default AdminSettingsComponent;